/**
 * DayEntryModal - Modal for viewing/editing a day's entry
 */

import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'framer-motion';
import { useMoodMapStore, MOODS, CATEGORIES } from '../../stores/moodmapStore';
import {
  X,
  Trash2,
  Sparkles,
  Plus,
  Loader2,
  Send,
} from 'lucide-react';
import MoodSelector from './MoodSelector';
import QuickTagChips from './QuickTagChips';
import CategorySection from './CategorySection';

const DayEntryModal = ({ isOpen, onClose }) => {
  const {
    selectedDate,
    selectedEntry,
    isParsing,
    isLoading,
    tags,
    saveMood,
    addItem,
    deleteItem,
    deleteEntry,
    parseInput,
    formatDate,
  } = useMoodMapStore();

  const [aiInput, setAiInput] = useState('');
  const [activeCategory, setActiveCategory] = useState('win');
  const [newItemContent, setNewItemContent] = useState('');
  const [selectedTagIds, setSelectedTagIds] = useState([]);
  const inputRef = useRef(null);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const handleAIParse = async () => {
    if (!aiInput.trim() || isParsing) return;
    await parseInput(aiInput);
    setAiInput('');
  };

  const handleAddItem = async () => {
    if (!newItemContent.trim()) return;
    try {
      await addItem(activeCategory, newItemContent, null);
      setNewItemContent('');
    } catch (error) {
      console.error('Error adding item:', error);
    }
  };

  const handleKeyDown = (e, action) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      action();
    }
  };

  const handleTagToggle = (tagId) => {
    setSelectedTagIds(prev =>
      prev.includes(tagId)
        ? prev.filter(id => id !== tagId)
        : [...prev, tagId]
    );
  };

  const handleQuickTagsSubmit = async () => {
    if (selectedTagIds.length === 0) return;

    try {
      // Add each selected tag as a separate item
      for (const tagId of selectedTagIds) {
        const tag = tags.find(t => t.id === tagId);
        if (tag) {
          // Use tag name as content, determine category from tag
          const category = tag.category === 'general' ? activeCategory : tag.category;
          await addItem(category, tag.name, tagId);
        }
      }
      setSelectedTagIds([]);
    } catch (error) {
      console.error('Error adding items from tags:', error);
    }
  };

  // Group items by category
  const itemsByCategory = {
    win: selectedEntry?.items?.filter((i) => i.category === 'win') || [],
    struggle: selectedEntry?.items?.filter((i) => i.category === 'struggle') || [],
    learning: selectedEntry?.items?.filter((i) => i.category === 'learning') || [],
    grateful: selectedEntry?.items?.filter((i) => i.category === 'grateful') || [],
  };

  // Don't render anything if not open
  if (!isOpen) return null;

  return createPortal(
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="w-full max-w-2xl max-h-[90vh] bg-gray-900 border border-gray-800 rounded-2xl shadow-2xl overflow-hidden flex flex-col"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-800">
          <div>
            <h2 className="text-lg font-semibold text-white">
              {selectedDate && formatDate(selectedDate)}
            </h2>
            <p className="text-sm text-gray-400">Günlük yansıma</p>
          </div>
          <div className="flex items-center gap-2">
            {selectedEntry && (
              <button
                onClick={deleteEntry}
                className="p-2 text-gray-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                title="Kaydı Sil"
              >
                <Trash2 className="w-5 h-5" />
              </button>
            )}
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-6">
          {/* Mood Selector */}
          <div>
            <p className="text-sm font-medium text-gray-400 mb-3">
              Bugün nasıl hissettin?
            </p>
            <MoodSelector
              selectedMood={selectedEntry?.mood}
              onSelect={saveMood}
            />
          </div>

          {/* AI Input */}
          <div className="bg-gray-800/50 border border-gray-700 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="w-4 h-4 text-purple-400" />
              <span className="text-sm font-medium text-gray-300">
                AI ile Ekle
              </span>
            </div>
            <div className="flex gap-2">
              <input
                ref={inputRef}
                type="text"
                value={aiInput}
                onChange={(e) => setAiInput(e.target.value)}
                onKeyDown={(e) => handleKeyDown(e, handleAIParse)}
                placeholder="Spor yaptım, toplantı iyi geçti ama yorgunum..."
                className="flex-1 bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:outline-none focus:border-purple-500 transition-colors"
                disabled={isParsing}
              />
              <button
                onClick={handleAIParse}
                disabled={!aiInput.trim() || isParsing}
                className="px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-700 disabled:cursor-not-allowed text-white rounded-lg transition-colors flex items-center gap-2"
              >
                {isParsing ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Doğal dilde yaz, AI otomatik kategorize etsin
            </p>
          </div>

          {/* Quick Tags */}
          <div>
            <p className="text-sm font-medium text-gray-400 mb-2">Hızlı Etiketler</p>
            <QuickTagChips
              category={activeCategory}
              selectedTagIds={selectedTagIds}
              onToggle={handleTagToggle}
              onSubmit={handleQuickTagsSubmit}
            />
          </div>

          {/* Manual Add */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <div className="flex bg-gray-800 rounded-lg p-1">
                {Object.entries(CATEGORIES).map(([key, config]) => (
                  <button
                    key={key}
                    onClick={() => setActiveCategory(key)}
                    className={`px-3 py-1.5 rounded-md text-sm transition-colors ${
                      activeCategory === key
                        ? 'bg-purple-600 text-white'
                        : 'text-gray-400 hover:text-white'
                    }`}
                  >
                    {config.emoji}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                value={newItemContent}
                onChange={(e) => setNewItemContent(e.target.value)}
                onKeyDown={(e) => handleKeyDown(e, handleAddItem)}
                placeholder={`${CATEGORIES[activeCategory].label} ekle...`}
                className="flex-1 bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:outline-none focus:border-purple-500 transition-colors"
              />
              <button
                onClick={handleAddItem}
                disabled={!newItemContent.trim()}
                className="px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-700 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
              >
                <Plus className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Category Sections */}
          <div className="space-y-4">
            {Object.entries(CATEGORIES).map(([category, config]) => (
              <CategorySection
                key={category}
                category={category}
                config={config}
                items={itemsByCategory[category]}
                onDelete={deleteItem}
              />
            ))}
          </div>
        </div>
      </motion.div>
    </motion.div>,
    document.body
  );
};

export default DayEntryModal;
